import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React, { useState } from 'react';
import propTypes from '../../../node_modules/prop-types/index.js';
import Icon from '../Icon/Icon.js';
import { safeRest, rem } from '../../utils/functions.js';
import { MESSAGE_VARIANT } from './constants.js';
import { MessageCardWrapper, IconBar, IconCircle, CloseButton, MessageTitle } from './styledComponents.js';
import useTheme from '../../hooks/useTheme.js';

var _excluded = ["id", "variant", "titleText", "children", "icon", "closeIcon", "hasCloseButton", "closeIconAriaLabel"];

var getDefaultIcon = function getDefaultIcon(variant, theme) {
  switch (variant) {
    case MESSAGE_VARIANT.PRIMARY_WARNING:
    case MESSAGE_VARIANT.PRIMARY_ALERT:
    case MESSAGE_VARIANT.SECONDARY_WARNING:
    case MESSAGE_VARIANT.SECONDARY_ALERT:
    case MESSAGE_VARIANT.HARDSTOP:
      return /*#__PURE__*/React.createElement(Icon, {
        manulifeName: "alert",
        fill: theme.colorPalette.neutralColors.light.shade_white
      });

    case MESSAGE_VARIANT.PRIMARY_COMPLETE:
    case MESSAGE_VARIANT.SECONDARY_COMPLETE:
      return /*#__PURE__*/React.createElement(Icon, {
        manulifeName: "checkmark",
        fill: theme.colorPalette.neutralColors.light.shade_white,
        width: rem(16),
        height: rem(16)
      });

    default:
      return /*#__PURE__*/React.createElement(Icon, {
        manulifeName: "info",
        fill: theme.colorPalette.neutralColors.light.shade_white
      });
  }
};

var MessageCard = function MessageCard(_ref) {
  var id = _ref.id,
      variant = _ref.variant,
      titleText = _ref.titleText,
      children = _ref.children,
      icon = _ref.icon,
      closeIcon = _ref.closeIcon,
      hasCloseButton = _ref.hasCloseButton,
      closeIconAriaLabel = _ref.closeIconAriaLabel,
      rest = _objectWithoutProperties(_ref, _excluded);

  var theme = useTheme();
  var safeProps = safeRest(rest);

  var _useState = useState(true),
      _useState2 = _slicedToArray(_useState, 2),
      isOpen = _useState2[0],
      setIsOpen = _useState2[1];

  var primaryVariants = [MESSAGE_VARIANT.PRIMARY_INFORMATION, MESSAGE_VARIANT.PRIMARY_ALERT, MESSAGE_VARIANT.PRIMARY_WARNING, MESSAGE_VARIANT.PRIMARY_COMPLETE];
  return /*#__PURE__*/React.createElement(MessageCardWrapper, _extends({}, safeProps, {
    id: id,
    variant: variant,
    isOpen: isOpen,
    theme: theme
  }), primaryVariants.indexOf(variant) !== -1 || variant === MESSAGE_VARIANT.HARDSTOP ? /*#__PURE__*/React.createElement(IconBar, {
    "aria-hidden": true,
    variant: variant,
    theme: theme
  }, icon || getDefaultIcon(variant, theme)) : /*#__PURE__*/React.createElement(IconCircle, {
    "aria-hidden": true,
    variant: variant,
    theme: theme
  }, icon || getDefaultIcon(variant, theme)), hasCloseButton && primaryVariants.indexOf(variant) !== -1 && /*#__PURE__*/React.createElement(CloseButton, {
    "aria-label": closeIconAriaLabel,
    onClick: function onClick() {
      return setIsOpen(false);
    }
  }, closeIcon), /*#__PURE__*/React.createElement(MessageTitle, null, titleText), /*#__PURE__*/React.createElement("div", null, children));
};

process.env.NODE_ENV !== "production" ? MessageCard.propTypes = {
  /** ID is required for Automated Testing, Google analytics and Google Tag Manager */
  id: propTypes.string,

  /** Which variant of the message component to display */
  variant: propTypes.oneOf([MESSAGE_VARIANT.PRIMARY_INFORMATION, MESSAGE_VARIANT.PRIMARY_WARNING, MESSAGE_VARIANT.PRIMARY_ALERT, MESSAGE_VARIANT.PRIMARY_COMPLETE, MESSAGE_VARIANT.SECONDARY_INFORMATION, MESSAGE_VARIANT.SECONDARY_WARNING, MESSAGE_VARIANT.SECONDARY_ALERT, MESSAGE_VARIANT.SECONDARY_COMPLETE, MESSAGE_VARIANT.HARDSTOP]),

  /** Title of the message */
  titleText: propTypes.string,

  /** Body of the message */
  children: propTypes.node,

  /** Icon to display left of message. Only specify if you need something different than the branding guide. */
  icon: propTypes.node,

  /** Is the message dismissable? */
  hasCloseButton: propTypes.bool,

  /** Icon to display as the close button. Only specify if you need something different than the branding guide,
   *  or if you need to specify the id (i.e. a fixed id is required for automated testing). */
  closeIcon: propTypes.node,

  /** Close icon to have aria-label for AODA, web accessibility. */
  closeIconAriaLabel: propTypes.string
} : void 0;
MessageCard.defaultProps = {
  id: undefined,
  variant: MESSAGE_VARIANT.PRIMARY_INFORMATION,
  titleText: '',
  children: '',
  icon: undefined,
  hasCloseButton: false,
  closeIcon: /*#__PURE__*/React.createElement(Icon, {
    manulifeName: "close"
  }),
  closeIconAriaLabel: ''
};
process.env.NODE_ENV !== "production" ? MessageCard.displayName = 'MessageCard' : void 0;

export default MessageCard;
